import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useP } from '../i18n/useP';
import '../styles/designSystem.css';

/* Filtre « on saisit, ça propose » — FORMAT STANDARD des filtres verticaux
   (personne, groupe, service…), décision 2026-08-27. On tape, la liste des
   valeurs existantes se réduit, on clique celle qu'on veut : elle devient un
   jeton retirable. Plusieurs jetons = plusieurs valeurs retenues.

   À préférer au menu de cases à cocher dès que la liste peut être longue :
   une liste de collaborateurs se parcourt en tapant trois lettres, pas en
   déroulant trente cases.

     <FilterSearch
       items={persons.map(p => ({ id: p.id, label: p.name }))}
       selected={sel} onChange={setSel}
       placeholder="Collaborateur…"
     />

   `items` = [{ id, label, kind? }] · `selected` = Set d'ids · `single` limite à
   une valeur (le jeton remplace le précédent).

   `kind` range les suggestions par NATURE sous un intertitre (« Groupe »,
   « Collaborateur »…) : un seul champ suffit alors à filtrer sur plusieurs
   dimensions, plutôt qu'un champ par dimension (décision 2026-08-27). */

function normalize(str) {
  return String(str || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

export default function FilterSearch({
  label = null,
  items = [],
  selected,
  onChange,
  placeholder = null,
  single = false,
  className = '',
}) {
  const p = useP();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (!boxRef.current || !boxRef.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);

  const needle = normalize(query).trim();
  const suggestions = useMemo(
    () => items.filter(it => !selected.has(it.id) && (!needle || normalize(it.label).includes(needle))),
    [items, selected, needle],
  );
  const chips = items.filter(it => selected.has(it.id));
  // Suggestions rangées par nature, dans l'ordre d'apparition des natures.
  const sections = useMemo(() => {
    const out = [];
    suggestions.forEach(it => {
      const kind = it.kind || '';
      let sec = out.find(s => s.kind === kind);
      if (!sec) { sec = { kind, items: [] }; out.push(sec); }
      sec.items.push(it);
    });
    return out;
  }, [suggestions]);

  const pick = (id) => {
    const next = single ? new Set([id]) : new Set(selected);
    if (!single) next.add(id);
    onChange(next);
    setQuery('');
    setOpen(false);
  };
  const drop = (id) => {
    const next = new Set(selected);
    next.delete(id);
    onChange(next);
  };

  return (
    <div className={`lk-ta${className ? ` ${className}` : ''}`} ref={boxRef}>
      {label && <span className="lk-filter-lbl">{label}</span>}
      {chips.map(c => (
        <span key={c.id} className="lk-ta__chip">
          {c.kind && <span className="lk-ta__chip-kind">{c.kind}</span>}
          {c.label}
          <button type="button" className="lk-ta__chip-x" onClick={() => drop(c.id)}
                  aria-label={`${p('Retirer')} ${c.label}`}>✕</button>
        </span>
      ))}
      <input
        type="search"
        className="lk-filter-input lk-ta__input"
        value={query}
        placeholder={placeholder || p('Rechercher…')}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={e => {
          if (e.key === 'Enter' && suggestions.length > 0) { e.preventDefault(); pick(suggestions[0].id); }
        }}
      />
      {open && (
        <div className="lk-ta__menu" role="listbox">
          {suggestions.length === 0 ? (
            <p className="lk-ta__empty">{p('Aucune valeur ne correspond.')}</p>
          ) : sections.map(sec => (
            <React.Fragment key={sec.kind || '_'}>
              {sec.kind && <p className="lk-ta__kind">{sec.kind}</p>}
              {sec.items.map(it => (
                <button key={it.id} type="button" role="option" aria-selected={false}
                        className="lk-ta__opt" onClick={() => pick(it.id)}>
                  {it.label}
                </button>
              ))}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}
